import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, IconButton, TextField, Button, Popover } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import RemoveIcon from '@mui/icons-material/Remove';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createLinkGroupApi,
  createLinkGroupItemsApi,
  deleteLinkGroupItemApi,
  getLinkGroupApi,
  getLinkGroupItemsApi,
  updateLinkGroupApi,
  updateLinkGroupItemApi,
} from '../../api';
import ConfirmModal from '../../components/common/ConfirmModal';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';

const measureTextWidth = (text, font) => {
  const canvas = measureTextWidth._canvas || (measureTextWidth._canvas = document.createElement('canvas'));
  const ctx = canvas.getContext('2d');
  if (!ctx) return 0;
  ctx.font = font;
  return ctx.measureText(text).width || 0;
};

const clampTextToPixelWidth = (text, maxPx, font) => {
  const s = (text ?? '').toString();
  if (!s) return '';
  if (measureTextWidth(s, font) <= maxPx) return s;
  let lo = 0;
  let hi = s.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    const part = s.slice(0, mid);
    if (measureTextWidth(part, font) <= maxPx) lo = mid;
    else hi = mid - 1;
  }
  return s.slice(0, lo);
};

const LinkGroupItemManagement = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const URL_MAX_LEN = 2048;
  const [categoryMaxPx, setCategoryMaxPx] = useState(120);
  const [titleMaxPx, setTitleMaxPx] = useState(120);

  const initialItems = useMemo(
    () => [{ id: 1, itemId: null, title: '', url: '' }],
    []
  );

  const [category, setCategory] = useState('');
  const [iconName, setIconName] = useState('');
  const [items, setItems] = useState(initialItems);
  const baseItemIdsRef = useRef([]);
  const [categoryError, setCategoryError] = useState(false);
  const [iconError, setIconError] = useState(false);
  const [itemErrors, setItemErrors] = useState({});
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [onConfirmAction, setOnConfirmAction] = useState(() => () => setConfirmOpen(false));
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const dragIndexRef = useRef(null);

  const emojiOpen = Boolean(emojiAnchorEl);
  const emojiPopoverId = emojiOpen ? 'icon-emoji-popover' : undefined;

  const openAlert = (message, onConfirm = null) => {
    setConfirmMessage(message);
    setOnConfirmAction(() => {
      return () => {
        setConfirmOpen(false);
        if (typeof onConfirm === 'function') onConfirm();
      };
    });
    setConfirmOpen(true);
  };

  const handleBack = () => navigate(-1);

  const handleOpenEmojiPicker = (e) => {
    setEmojiAnchorEl(e.currentTarget);
  };

  const handleCloseEmojiPicker = () => {
    setEmojiAnchorEl(null);
  };

  const handleAddItem = () => {
    setItems((prev) => {
      if ((prev?.length || 0) >= 3) return prev;
      return [...prev, { id: Date.now(), itemId: null, title: '', url: '' }];
    });
  };

  const handleRemoveItem = (id) => {
    setItems((prev) => {
      const next = prev.filter((x) => x.id !== id);
      return next.length > 0 ? next : initialItems;
    });
  };

  const handleChangeItem = (id, key, value) => {
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, [key]: value } : x)));
  };

  const handleDragStart = (index) => {
    setDragIndex(index);
    dragIndexRef.current = index;
  };

  const handleDragOver = (event, index) => {
    event.preventDefault();
    const currentIndex = dragIndexRef.current;
    if (currentIndex === null || currentIndex === index) return;

    setItems((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(currentIndex, 1);
      updated.splice(index, 0, moved);
      return updated;
    });

    setDragIndex(index);
    dragIndexRef.current = index;
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    dragIndexRef.current = null;
  };

  const handleTouchStart = (index) => {
    setDragIndex(index);
    dragIndexRef.current = index;
  };

  const handleTouchMove = (e) => {
    if (e.cancelable) e.preventDefault();
    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!element) return;

    const row = element.closest('[data-index]');
    if (!row) return;

    const newIndex = parseInt(row.getAttribute('data-index'), 10);
    const currentIndex = dragIndexRef.current;
    if (Number.isNaN(newIndex) || currentIndex === null || currentIndex === newIndex) return;

    setItems((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(currentIndex, 1);
      updated.splice(newIndex, 0, moved);
      return updated;
    });

    setDragIndex(newIndex);
    dragIndexRef.current = newIndex;
  };

  const handleTouchEnd = () => {
    setDragIndex(null);
    dragIndexRef.current = null;
  };

  useEffect(() => {
    const compute = () => {
      const parentWidth = Math.min(398, Math.max(0, (window?.innerWidth || 430) - 32));
      const gapPx = 1.8 * 8;
      const colWidth = Math.max(0, (parentWidth - gapPx) / 2);
      const cardPaddingLR = 2.5 * 8 * 2;
      const iconWidth = 24;
      const iconGap = 0.8 * 8;
      const maxPx = Math.max(60, Math.floor(colWidth - cardPaddingLR - iconWidth - iconGap));
      setCategoryMaxPx(maxPx);

      const badgeWidth = 22;
      const badgeMr = 1.2 * 8;
      const arrowArea = 20 + (0.25 * 8 * 2) + (1 * 8);
      const titlePx = Math.max(60, Math.floor(colWidth - cardPaddingLR - badgeWidth - badgeMr - arrowArea));
      setTitleMaxPx(titlePx);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  useEffect(() => {
    if (!isEditMode) {
      baseItemIdsRef.current = [];
      setCategory('');
      setIconName('');
      setCategoryError(false);
      setIconError(false);
      setItemErrors({});
      setItems(initialItems);
      return;
    }

    let alive = true;
    (async () => {
      try {
        const groupId = id;
        const [groupRes, itemsRes] = await Promise.all([
          getLinkGroupApi(groupId),
          getLinkGroupItemsApi({ groupId }),
        ]);

        const group = groupRes?.data || groupRes;
        const list = itemsRes?.data || itemsRes || [];

        const loadedItems = (list || []).map((it) => ({
          id: it.itemId,
          itemId: it.itemId,
          title: it.title || '',
          url: it.url || '',
          sortOrder: it.sortOrder,
          rankLabel: it.rankLabel,
        }));

        if (!alive) return;
        setCategory(group?.name || '');
        setIconName(group?.iconName || '');
        setCategoryError(false);
        setIconError(false);
        setItemErrors({});
        baseItemIdsRef.current = loadedItems.map((x) => x.itemId).filter(Boolean);
        setItems(loadedItems.length > 0 ? loadedItems.slice(0, 3) : initialItems);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id, initialItems, isEditMode]);

  const validateForm = () => {
    const nextCategoryError = !(category || '').trim();
    const nextIconError = !(iconName || '').trim();

    const errs = {};
    let hasAnyCompleteItem = false;
    let hasTooLongUrl = false;

    (items || []).slice(0, 3).forEach((it) => {
      const t = (it?.title || '').trim();
      const u = (it?.url || '').trim();
      const hasAny = Boolean(t || u);
      const isComplete = Boolean(t && u);
      if (isComplete) hasAnyCompleteItem = true;

      if (hasAny && !isComplete) {
        errs[it.id] = { title: !t, url: !u };
      }

      if (u && u.length > URL_MAX_LEN) {
        hasTooLongUrl = true;
        errs[it.id] = { ...(errs[it.id] || {}), url: true };
      }
    });

    if (!hasAnyCompleteItem) {
      const first = (items || [])[0];
      if (first?.id !== undefined) {
        errs[first.id] = { title: true, url: true };
      }
    }

    setCategoryError(nextCategoryError);
    setIconError(nextIconError);
    setItemErrors(errs);

    return !nextCategoryError && !nextIconError && !hasTooLongUrl && hasAnyCompleteItem && Object.keys(errs).length === 0;
  };

  const processSubmit = async () => {
    if (submitting) return;
    setSaveModalOpen(false);
    if (!validateForm()) return;
    setSubmitting(true);
    try {
        const trimmedCategory = (category || '').trim();
        const groupId = id;

        if (isEditMode) {
          await updateLinkGroupApi(groupId, { name: trimmedCategory, iconName });

          const filled = (items || [])
            .filter((x) => x && x.title && x.url)
            .slice(0, 3);

          const currentIds = filled.map((x) => x.itemId).filter(Boolean);
          const toDelete = (baseItemIdsRef.current || []).filter((baseId) => !currentIds.includes(baseId));
          if (toDelete.length > 0) {
            await Promise.all(toDelete.map((itemId) => deleteLinkGroupItemApi(itemId)));
          }

          const createPayload = [];
          const updatePayload = [];
          filled.forEach((x, idx) => {
            const payload = {
              title: x.title,
              url: x.url,
              sortOrder: idx + 1,
              rankLabel: `${idx + 1}위`,
            };
            if (x.itemId) updatePayload.push({ itemId: x.itemId, payload });
            else createPayload.push(payload);
          });

          if (updatePayload.length > 0) {
            await Promise.all(updatePayload.map((x) => updateLinkGroupItemApi(x.itemId, x.payload)));
          }
          if (createPayload.length > 0) {
            await createLinkGroupItemsApi({ groupId, items: createPayload });
          }

          openAlert('수정 완료', () => navigate(-1));
          return;
        }

        const groupRes = await createLinkGroupApi({ name: trimmedCategory, iconName });
        const group = groupRes?.data;
        const createdGroupId = group?.groupId;
        if (!createdGroupId) throw new Error('groupId 생성 실패');

        const payloadItems = (items || [])
          .filter((x) => x && x.title && x.url)
          .slice(0, 3)
          .map((x, idx) => ({
            title: x.title,
            url: x.url,
            sortOrder: idx + 1,
            rankLabel: `${idx + 1}위`,
          }));

        if (payloadItems.length > 0) await createLinkGroupItemsApi({ groupId: createdGroupId, items: payloadItems });
        openAlert('등록 완료', () => navigate(-1));
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    setSaveModalOpen(true);
  };

  return (
    <Box>
      <ConfirmModal
        open={confirmOpen}
        message={confirmMessage}
        confirmText="확인"
        onConfirm={onConfirmAction}
      />
      <ConfirmModal
        open={saveModalOpen}
        message={isEditMode ? '링크를 수정하시겠습니까?' : '링크를 등록하시겠습니까?'}
        confirmText={isEditMode ? '수정' : '등록'}
        onConfirm={processSubmit}
        onCancel={() => setSaveModalOpen(false)}
      />
      <Box
        sx={{
          maxWidth: 430,
          mx: 'auto',
          bgcolor: 'white',
          minHeight: '100vh',
          borderRadius: 0,
          boxShadow: '0 0 20px rgba(0,0,0,0.06)',
          px: 2,
          position: 'relative',
          pb: 10,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 60,
            borderBottom: '1px solid #e0e0e0',
            mb: 3,
            mx: -2,
            px: 2,
            position: 'relative',
          }}
        >
          <IconButton onClick={handleBack} sx={{ p: 0, position: 'absolute', left: 16 }}>
            <ArrowBackIosNewIcon sx={{ fontSize: 20, color: '#333' }} />
          </IconButton>
          <Typography
            sx={{
              flex: 1,
              textAlign: 'center',
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            {isEditMode ? '링크 수정' : '링크 등록'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, whiteSpace: 'nowrap', minWidth: 48 }}>
              카테고리
            </Typography>
            <TextField
              value={category}
              onChange={(e) => {
                const nextRaw = e.target.value;
                const font = '700 16.5px Roboto, Helvetica, Arial, sans-serif';
                const next = clampTextToPixelWidth(nextRaw, categoryMaxPx, font);
                setCategory(next);
                if (categoryError) setCategoryError(false);
              }}
              fullWidth
              size="small"
              error={categoryError}
              sx={{
                '& .MuiOutlinedInput-root': { fontSize: 14, '& input': { py: 1.2 } },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, whiteSpace: 'nowrap', minWidth: 48 }}>
              아이콘
            </Typography>
            <TextField
              value={iconName}
              inputProps={{ readOnly: true }}
              onClick={handleOpenEmojiPicker}
              placeholder="클릭해서 선택해주세요."
              fullWidth
              size="small"
              error={iconError}
              sx={{
                '& .MuiOutlinedInput-root': { fontSize: 14, '& input': { py: 1.2 } },
              }}
            />
          </Box>

          <Popover
            id={emojiPopoverId}
            open={emojiOpen}
            anchorEl={emojiAnchorEl}
            onClose={handleCloseEmojiPicker}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: 2,
                overflow: 'hidden',
              },
            }}
          >
            <Picker
              data={data}
              previewPosition="none"
              onEmojiSelect={(emoji) => {
                setIconName(emoji?.native || '');
                handleCloseEmojiPicker();
              }}
            />
          </Popover>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700 }}
            >
              리스트
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
              {items.map((it, index) => {
                const isDragging = dragIndex === index;
                const rowErr = itemErrors[it.id] || {};
                return (
                <Box
                  key={it.id}
                  data-index={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: isDragging ? '2px solid #0d47a1' : '1px solid #E5E7EB',
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.6,
                    gap: 1,
                    bgcolor: '#fff',
                    transform: isDragging ? 'scale(1.02)' : 'scale(1)',
                    boxShadow: isDragging ? '0 4px 10px rgba(0,0,0,0.10)' : 'none',
                    transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
                    position: 'relative',
                    zIndex: isDragging ? 1 : 'auto',
                  }}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={handleDragEnd}
                >
                  <Box
                    draggable
                    sx={{ display: 'flex', alignItems: 'center', mr: 1, touchAction: 'none' }}
                    onDragStart={() => handleDragStart(index)}
                    onDragEnd={handleDragEnd}
                    onTouchStart={() => handleTouchStart(index)}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  >
                    <DragHandleIcon sx={{ fontSize: 20, color: '#9CA3AF', cursor: 'grab' }} />
                  </Box>
                  <TextField
                    value={it.title}
                    onChange={(e) => {
                      const nextRaw = e.target.value;
                      const font = '500 15px Roboto, Helvetica, Arial, sans-serif';
                      const next = clampTextToPixelWidth(nextRaw, titleMaxPx, font);
                      handleChangeItem(it.id, 'title', next);
                      if (itemErrors[it.id]?.title) {
                        setItemErrors((prev) => {
                          const next = { ...prev };
                          next[it.id] = { ...(next[it.id] || {}), title: false };
                          if (!next[it.id].title && !next[it.id].url) delete next[it.id];
                          return next;
                        });
                      }
                    }}
                    size="small"
                    variant="standard"
                    placeholder="사이트명"
                    error={Boolean(rowErr.title)}
                    InputProps={{
                      disableUnderline: true,
                      sx: { fontSize: 14, fontWeight: 500, px: 0.5, py: 0.2 },
                    }}
                    sx={{
                      width: 95,
                      minWidth: 95,
                      '& .MuiInputBase-root': {
                        bgcolor: 'transparent',
                        border: (theme) =>
                          rowErr.title ? `1px solid ${theme.palette.error.main}` : '1px solid transparent', borderRadius: 1,
                      },
                    }}
                  />
                  <TextField
                    value={it.url}
                    onChange={(e) => {
                      handleChangeItem(it.id, 'url', e.target.value);
                      if (itemErrors[it.id]?.url) {
                        setItemErrors((prev) => {
                          const next = { ...prev };
                          next[it.id] = { ...(next[it.id] || {}), url: false };
                          if (!next[it.id].title && !next[it.id].url) delete next[it.id];
                          return next;
                        });
                      }
                    }}
                    size="small"
                    fullWidth
                    variant="standard"
                    placeholder="URL"
                    error={Boolean(rowErr.url)}
                    InputProps={{
                      disableUnderline: true,
                      sx: { fontSize: 14, fontWeight: 500, px: 0.5, py: 0.2 },
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        bgcolor: 'transparent',
                        border: (theme) =>
                          rowErr.url ? `1px solid ${theme.palette.error.main}` : '1px solid transparent', borderRadius: 1,
                      },
                    }}
                  />
                  <IconButton
                    onClick={() => handleRemoveItem(it.id)}
                    size="small"
                    sx={{
                      ml: 0.5,
                      color: '#9CA3AF',
                      '&:hover': { color: '#333', bgcolor: 'transparent' },
                    }}
                  >
                    <RemoveIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
              );
              })}

              {items.length < 3 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <AddCircleOutlineIcon
                    sx={{ fontSize: 30, color: '#999', cursor: 'pointer' }}
                    onClick={handleAddItem}
                  />
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              disabled={submitting}
              sx={{ py: 1.2, bgcolor: '#0d47a1', fontWeight: 600, '&:hover': { bgcolor: '#0b3a81' } }}
            >
              {isEditMode ? '수정' : '등록'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LinkGroupItemManagement;


